import { useState, useEffect, useRef, useContext } from 'react';
import { Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { deleteDoc, DocumentReference } from 'firebase/firestore';
import './Modal.css';
import { AuthenticatedSessionContext } from '../authenticatedSession/AuthenticatedSessionContext';
import { NoteData } from '../database/baseTypes';
import { useMatterNoteAdd } from '../database/hooks/hooks';
import MatterAutocomplete, { Matter } from './MatterAutocomplete';
import { FirmDatabase } from '../database/firmDatabase';
import { NoteEditor } from './NoteEditor';
import { QuickEntryModal } from './QuickEntryModal/QuickEntryModal';

interface Props {
  fixedMatterId: string | undefined;
  onClose: () => void;
}

const NotesModal = ({ fixedMatterId, onClose }: Props) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const modalRef = useRef<HTMLDivElement>(null);
  const addMatterNote = useMatterNoteAdd();
  const [noteContent, setNoteContent] = useState('');
  const [selectedMatter, setSelectedMatter] = useState<Matter | undefined>(
    undefined,
  );

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleUndo = async (
    refToDelete: DocumentReference<NoteData> | undefined,
  ) => {
    if (!refToDelete) {
      return;
    }

    await deleteDoc(refToDelete);
    notifications.show({
      title: 'Undo successful',
      message: 'Note has been removed',
      color: 'blue.4',
      autoClose: 1500,
    });
  };

  const handleSaveNote = async (firmDb: FirmDatabase | undefined) => {
    if (!firmDb) {
      console.warn('Attempting to save note without a database');
      notifications.show({
        title: 'Unable to add note',
        message: 'Please log in to add a note',
        color: 'red.4',
        autoClose: 6000,
      });
      onClose();
      return;
    }

    if (!fixedMatterId && !selectedMatter) {
      notifications.show({
        title: 'Missing required fields',
        message: 'Please select a matter',
        color: 'red.4',
        autoClose: 6000,
      });
      return;
    }

    if (!authSession) {
      notifications.show({
        title: 'Unable to add note',
        message: 'Please log in to add a note',
        color: 'red.4',
        autoClose: 6000,
      });
      return;
    }

    const matterId = fixedMatterId ?? selectedMatter!.id;

    const ref = await addMatterNote(matterId, {
      content: noteContent,
      createdTimestamp: new Date(),
      userId: authSession.userIds.firestore,
    });
    const successNotificationId = `new-note-entry-${ref?.id ?? Date.now()}`;

    notifications.show({
      id: successNotificationId,
      title: 'Note created 📝',
      message: (
        <Group justify='space-between' wrap='nowrap'>
          <span>New note created successfully</span>
          <Button
            variant='white'
            color='dark'
            size='compact-xs'
            onClick={async () => {
              try {
                await handleUndo(ref);
                notifications.hide(successNotificationId);
              } catch (error) {
                console.error('Failed to undo note creation', error);
                notifications.show({
                  title: 'Undo failed',
                  message:
                    'The note could not be removed. Please check your connection and try again.',
                  color: 'red.4',
                  autoClose: 6000,
                });
              }
            }}
          >
            Undo
          </Button>
        </Group>
      ),
      color: 'blue.4',
      autoClose: 6000,
    });

    onClose();
  };

  return (
    <QuickEntryModal
      title='New Note'
      onClose={onClose}
      onSave={() => handleSaveNote(authSession?.db)}
    >
      <div style={{ width: '100%' }}>
        {!fixedMatterId && (
          <MatterAutocomplete
            firmDb={authSession?.db}
            value={selectedMatter}
            error={!selectedMatter && !fixedMatterId}
            onSelect={setSelectedMatter}
          />
        )}
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <NoteEditor
            value={noteContent}
            onChange={setNoteContent}
            placeholder='Write a note…'
            autoFocus
          />
        </div>
      </div>
    </QuickEntryModal>
  );
};

export default NotesModal;
